/**
 * R-63 — LLM-as-judge topical drift detector (JSON verdict).
 */
import { Type } from '@google/genai';
import { GEMINI_PRO_MODEL } from '../../config/geminiModels';
import { ai } from '../../services/geminiClient';
import type { SectionDriftRegistryEntry } from './sectionDriftRegistry';

export interface LlmJudgeDriftFinding {
  detector: 'llm-judge';
  reportTypeId: string;
  sectionId: string;
  severity: 'block' | 'warn';
  score: number;
  rationale: string;
  message: string;
}

export type LlmJudgeFn = (
  sectionText: string,
  registryEntry: SectionDriftRegistryEntry,
) => Promise<{ score: number; rationale: string } | null>;

const JUDGE_MODEL = GEMINI_PRO_MODEL;

function buildJudgePrompt(sectionText: string, entry: SectionDriftRegistryEntry): string {
  return [
    'You are a report QA judge. Score how well the SECTION TEXT matches the CANONICAL DESCRIPTION for this report section.',
    'Return JSON only.',
    '',
    `Report type: ${entry.reportTypeId}`,
    `Section id: ${entry.sectionId}`,
    `Canonical description: ${entry.canonicalDescription}`,
    `Required topical keywords (expect coverage): ${entry.requiredKeywords.join(', ')}`,
    `Prohibited off-topic signals: ${entry.prohibitedKeywords.join(', ')}`,
    '',
    'Scoring rubric (integer 1-10):',
    '10 = fully on-topic, investor-grade, matches section intent',
    '7-9 = mostly on-topic with minor gaps',
    '4-6 = partial drift or mixed topics',
    '1-3 = wrong section / severe cross-type bleed / boilerplate',
    '',
    'SECTION TEXT:',
    sectionText.slice(0, 6000),
  ].join('\n');
}

function parseJudgeResponse(text: string | undefined | null): { score: number; rationale: string } | null {
  if (!text?.trim()) return null;
  try {
    const parsed = JSON.parse(text) as { score?: number; rationale?: string };
    const score = typeof parsed.score === 'number' ? parsed.score : Number(parsed.score);
    if (!Number.isFinite(score)) return null;
    return {
      score: Math.max(1, Math.min(10, Math.round(score))),
      rationale: typeof parsed.rationale === 'string' ? parsed.rationale : '',
    };
  } catch {
    return null;
  }
}

/** Default Gemini judge — overridable in tests. */
export const defaultLlmJudgeFn: LlmJudgeFn = async (sectionText, entry) => {
  const prompt = buildJudgePrompt(sectionText, entry);
  try {
    const response = await ai.models.generateContent({
      model: JUDGE_MODEL,
      contents: prompt,
      config: {
        responseMimeType: 'application/json',
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            score: { type: Type.NUMBER, description: 'Integer topical alignment score 1-10' },
            rationale: { type: Type.STRING, description: 'One-sentence justification' },
          },
          required: ['score', 'rationale'],
        },
      },
    });
    return parseJudgeResponse(response.text);
  } catch (err) {
    console.warn('[llmJudgeDrift] generateContent failed:', (err as Error)?.message);
    return null;
  }
};

export async function detectLlmJudgeDrift(
  sectionText: string,
  registryEntry: SectionDriftRegistryEntry,
  judgeFn: LlmJudgeFn = defaultLlmJudgeFn,
): Promise<LlmJudgeDriftFinding[]> {
  const text = sectionText.trim();
  if (!text || text.length < 40) return [];

  const verdict = await judgeFn(text, registryEntry);
  if (!verdict) return [];

  let severity: 'block' | 'warn' | null = null;
  if (verdict.score <= 3) severity = 'block';
  else if (verdict.score <= 6) severity = 'warn';
  if (!severity) return [];

  return [{
    detector: 'llm-judge',
    reportTypeId: registryEntry.reportTypeId,
    sectionId: registryEntry.sectionId,
    severity,
    score: verdict.score,
    rationale: verdict.rationale,
    message: `Section "${registryEntry.sectionId}" LLM judge score ${verdict.score}/10 — ${verdict.rationale}`,
  }];
}
